'use client';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _extends from "@babel/runtime/helpers/esm/extends";
const _excluded = ["items", "overlay", "renderItem", "getItemKey", "estimatedItemSize", "onReachTop", "onReachBottom", "autoScroll", "enableRovingFocus", "slots", "slotProps"];
import * as React from 'react';
import useSlotProps from '@mui/utils/useSlotProps';
import useEnhancedEffect from '@mui/utils/useEnhancedEffect';
import { useChat } from "../hooks/useChat.js";
import { useChatStore } from "../hooks/useChatStore.js";
import { useMessageIds } from "../hooks/useMessage.js";
import { useChatLocaleText } from "../chat/internals/ChatLocaleContext.js";
import { useRovingFocus } from "../internals/useRovingFocus.js";
import { useMessageListBehavior } from "./useMessageListBehavior.js";
import { ScrollRoot, ScrollViewport, ScrollScrollbar, ScrollThumb, scrollbarStyle, thumbStyle } from "../internals/ScrollAreaSlots.js";
import { MessageListContextProvider } from "./internals/MessageListContext.js";
import { MessageRovingProvider, useMessageRovingController } from "./internals/MessageRovingContext.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const DEFAULT_ESTIMATED_ITEM_SIZE = 84;
const DEFAULT_AUTO_SCROLL_BUFFER = 150;
function MessageListRenderedRow(props) {
  const {
    id,
    index,
    renderItem,
    registerRowElement,
    onRowResize
  } = props;
  const rowRef = React.useRef(null);
  useEnhancedEffect(() => {
    registerRowElement(id, rowRef.current);
    return () => {
      registerRowElement(id, null);
    };
  }, [id, registerRowElement]);
  useEnhancedEffect(() => {
    if (!rowRef.current || typeof globalThis.ResizeObserver === 'undefined') {
      return undefined;
    }
    let frameId = 0;
    const observer = new globalThis.ResizeObserver(() => {
      if (typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(frameId);
      }
      if (typeof requestAnimationFrame !== 'function') {
        onRowResize();
        return;
      }
      frameId = requestAnimationFrame(() => {
        onRowResize();
      });
    });
    observer.observe(rowRef.current);
    return () => {
      if (typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(frameId);
      }
      observer.disconnect();
    };
  }, [onRowResize]);
  return /*#__PURE__*/_jsx("div", {
    "data-message-id": id,
    "data-message-list-row": "",
    ref: rowRef,
    style: {
      width: '100%'
    },
    children: renderItem({
      id,
      index
    })
  });
}

// ---------------------------------------------------------------------------
// Functional default styles for the scroll slots.
// These are applied via `additionalProps` so consumers can override them
// through `slotProps` or direct `style` props (which take higher priority
// in the `mergeSlotProps` merge order).
// ---------------------------------------------------------------------------

const scrollRootStyle = {
  overflow: 'hidden',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0
};
const scrollViewportStyle = {
  overscrollBehavior: 'contain',
  paddingRight: 8
};
const overlayStyle = {
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  pointerEvents: 'none'
};

// Standard visually-hidden recipe: present in the accessibility tree (the
// status live region must stay rendered) but invisible and out of layout.
const visuallyHiddenStyle = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0
};

// Fallback styles used when the outer `messageList` slot is overridden
// (which removes the ScrollArea context).
const fallbackRootStyle = {
  position: 'relative',
  minHeight: 0
};
const fallbackScrollerStyle = {
  overflowY: 'auto',
  overscrollBehavior: 'contain'
};
function StaticMessageListView(props) {
  const {
    itemIds,
    overlay,
    renderItem,
    getItemKey,
    statusAnnouncement,
    slots,
    slotProps,
    other,
    behavior
  } = props;
  const localeText = useChatLocaleText();
  // When the outer messageList slot is overridden, the ScrollArea context is
  // absent, so the scroller / scrollbar must fall back to plain elements.
  const hasScrollArea = slots?.messageList == null;
  const MessageList = slots?.messageList ?? ScrollRoot;
  const MessageListScroller = slots?.messageListScroller ?? (hasScrollArea ? ScrollViewport : 'div');
  const MessageListContent = slots?.messageListContent ?? 'div';
  const MessageListOverlay = slots?.messageListOverlay ?? 'div';
  const MessageListScrollbar = slots?.messageListScrollbar ?? (hasScrollArea ? ScrollScrollbar : 'div');
  const MessageListScrollbarThumb = slots?.messageListScrollbarThumb ?? (hasScrollArea ? ScrollThumb : 'div');
  const MessageListStatus = slots?.messageListStatus ?? 'div';
  const messageListProps = useSlotProps({
    elementType: MessageList,
    externalSlotProps: slotProps?.messageList,
    ownerState: behavior.ownerState,
    additionalProps: {
      style: hasScrollArea ? scrollRootStyle : fallbackRootStyle
    }
  });
  const messageListScrollerProps = useSlotProps({
    elementType: MessageListScroller,
    externalSlotProps: slotProps?.messageListScroller,
    externalForwardedProps: other,
    ownerState: behavior.ownerState,
    additionalProps: {
      ref: behavior.setRootElement,
      role: 'log',
      'aria-label': localeText.messageListLabel,
      'aria-live': 'polite',
      onScroll: behavior.handleScroll,
      style: hasScrollArea ? scrollViewportStyle : fallbackScrollerStyle
    }
  });
  const messageListContentProps = useSlotProps({
    elementType: MessageListContent,
    externalSlotProps: slotProps?.messageListContent,
    ownerState: behavior.ownerState
  });
  const messageListOverlayProps = useSlotProps({
    elementType: MessageListOverlay,
    externalSlotProps: slotProps?.messageListOverlay,
    ownerState: behavior.ownerState,
    additionalProps: {
      style: overlayStyle
    }
  });
  const scrollbarProps = useSlotProps({
    elementType: MessageListScrollbar,
    externalSlotProps: slotProps?.messageListScrollbar,
    ownerState: behavior.ownerState,
    additionalProps: {
      orientation: 'vertical',
      style: scrollbarStyle
    }
  });
  const thumbProps = useSlotProps({
    elementType: MessageListScrollbarThumb,
    externalSlotProps: slotProps?.messageListScrollbarThumb,
    ownerState: behavior.ownerState,
    additionalProps: {
      style: thumbStyle
    }
  });
  const statusProps = useSlotProps({
    elementType: MessageListStatus,
    externalSlotProps: slotProps?.messageListStatus,
    ownerState: behavior.ownerState,
    additionalProps: {
      // `role="status"` is an implicit polite, atomic live region. It only
      // ever carries the short streaming-transition announcements (never the
      // streamed content itself), so it cannot spam screen readers per token.
      role: 'status',
      style: visuallyHiddenStyle
    }
  });
  return /*#__PURE__*/_jsx(MessageListContextProvider, {
    value: behavior.contextValue,
    children: /*#__PURE__*/_jsxs(MessageList, _extends({}, messageListProps, {
      children: [/*#__PURE__*/_jsx(MessageListScroller, _extends({}, messageListScrollerProps, {
        children: /*#__PURE__*/_jsx(MessageListContent, _extends({}, messageListContentProps, {
          children: itemIds.map((id, index) => /*#__PURE__*/_jsx(MessageListRenderedRow, {
            id: id,
            index: index,
            onRowResize: behavior.scheduleResizeRestore,
            registerRowElement: behavior.registerRowElement,
            renderItem: renderItem
          }, getItemKey(id, index)))
        }))
      })), hasScrollArea ? /*#__PURE__*/_jsx(MessageListScrollbar, _extends({}, scrollbarProps, {
        children: /*#__PURE__*/_jsx(MessageListScrollbarThumb, _extends({}, thumbProps))
      })) : null, overlay != null ? /*#__PURE__*/_jsx(MessageListOverlay, _extends({}, messageListOverlayProps, {
        children: overlay
      })) : null, /*#__PURE__*/_jsx(MessageListStatus, _extends({}, statusProps, {
        children: statusAnnouncement
      }))]
    }))
  });
}
export const MessageListRoot = /*#__PURE__*/React.forwardRef(function MessageListRoot(props, ref) {
  const {
      items: itemsProp,
      overlay = null,
      renderItem,
      getItemKey = id => id,
      estimatedItemSize = DEFAULT_ESTIMATED_ITEM_SIZE,
      onReachTop,
      onReachBottom,
      autoScroll = true,
      enableRovingFocus = true,
      slots,
      slotProps
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const defaultItems = useMessageIds();
  const {
    hasMoreHistory,
    loadMoreHistory,
    messages,
    isStreaming: isAdapterStreaming
  } = useChat();
  const store = useChatStore();
  const itemIds = itemsProp ?? defaultItems;

  // One tab stop for the whole list: a roving tabindex over the message
  // articles. No `onActivate` (Enter is drill-in, not selection), no
  // type-ahead (messages have no short stable label), and no Page keys
  // (PageUp/PageDown keep their native scrolling — the only keyboard way to
  // read a message taller than the viewport). `fallback: 'last'` keeps the
  // tab stop on the newest message until the user interacts.
  const roving = useRovingFocus({
    itemIds,
    restoreKey: store,
    scope: 'message-list',
    fallback: 'last',
    enablePageKeys: false,
    restoreFocusOnMount: false
  });
  const rovingContextValue = useMessageRovingController({
    enabled: enableRovingFocus,
    roving
  });

  // Combine adapter-level streaming (active stream via processStream) with
  // message-level streaming (any message with status 'streaming').  This
  // ensures auto-scroll on resize works for externally-managed messages
  // (e.g. the Captions demo) that set status: 'streaming' without going
  // through the adapter stream flow.
  const isAnyMessageStreaming = messages.some(m => m.status === 'streaming');
  const isStreaming = isAdapterStreaming || isAnyMessageStreaming;

  // Announce streaming transitions (start / complete) through the
  // visually-hidden status live region. Only transitions are announced —
  // never the streamed content — so screen readers hear exactly two short
  // messages per response instead of every token. The streaming article
  // itself additionally carries `aria-busy` (see MessageRoot).
  const localeText = useChatLocaleText();
  const [statusAnnouncement, setStatusAnnouncement] = React.useState('');
  const prevIsStreamingRef = React.useRef(isStreaming);
  React.useEffect(() => {
    if (prevIsStreamingRef.current === isStreaming) {
      return;
    }
    prevIsStreamingRef.current = isStreaming;
    setStatusAnnouncement(isStreaming ? localeText.responseStreamingStartedAnnouncement : localeText.responseStreamingCompletedAnnouncement);
  }, [isStreaming, localeText]);
  const autoScrollEnabled = autoScroll !== false;
  let autoScrollBuffer;
  if (!autoScrollEnabled) {
    // fall back to estimatedItemSize so isAtBottom tracking still works
    autoScrollBuffer = estimatedItemSize;
  } else if (typeof autoScroll === 'object') {
    autoScrollBuffer = autoScroll.buffer ?? DEFAULT_AUTO_SCROLL_BUFFER;
  } else {
    autoScrollBuffer = DEFAULT_AUTO_SCROLL_BUFFER;
  }
  const behavior = useMessageListBehavior({
    itemIds,
    estimatedItemSize,
    onReachTop,
    onReachBottom,
    messages,
    hasMoreHistory,
    loadMoreHistory,
    autoScrollEnabled,
    autoScrollBuffer,
    isStreaming
  });
  const {
    setFocusedId,
    focusItem
  } = roving;
  React.useImperativeHandle(ref, () => ({
    scrollToBottom: behavior.scrollToBottom,
    focusMessage: id => {
      const targetId = id ?? itemIds[itemIds.length - 1];
      if (targetId == null) {
        return;
      }
      setFocusedId(targetId);
      focusItem(targetId);
    }
  }), [behavior.scrollToBottom, focusItem, itemIds, setFocusedId]);
  return /*#__PURE__*/_jsx(MessageRovingProvider, {
    value: rovingContextValue,
    children: /*#__PURE__*/_jsx(StaticMessageListView, {
      behavior: behavior,
      getItemKey: getItemKey,
      itemIds: itemIds,
      overlay: overlay,
      other: other,
      renderItem: renderItem,
      slotProps: slotProps,
      slots: slots,
      statusAnnouncement: statusAnnouncement
    })
  });
});
if (process.env.NODE_ENV !== "production") MessageListRoot.displayName = "MessageListRoot";